import {
  ACESFilmicToneMapping,
  AgXToneMapping,
  Box3,
  Color,
  Frustum,
  Matrix4,
  Sphere,
  LinearToneMapping,
  type Mesh,
  NeutralToneMapping,
  NoToneMapping,
  PerspectiveCamera,
  ReinhardToneMapping,
  Scene,
  type ToneMapping,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';
import { config } from './config';
import { backgroundOf, pickTier, type Manifest, type TierName } from './scene/manifest';
import { homeRoom, loadRoomScene, loadRooms, namedRoom, roomFromUrl, urlForRoom, useRooms, type RoomScene, type SceneChoice } from './scene/registry';
import { loadExperience, withoutExperience } from './room/experience';
import { arrival } from './room/arrival';
import { Portals, PORTAL_PREFIX, portalSpawn, shapeNodes, type ActivePortal } from './room/portals';
import { disposeScene, loadScene, releaseTextureData, type LoadedScene } from './scene/loader';
import { RoomShading } from './render/room-shading';
import { RetroLook, roomLook } from './render/retro';
import { Artworks } from './art/artworks';
import { ArtPanel } from './art/art-panel';
import { SlotOutline } from './art/outline';
import { SlotSet } from './art/slots';
import { frameless, OVERLAY_KIND, SURFACES } from './art/artworks';
import { ScreenOverlay } from './overlay/overlay';
import { crossSiteUrl, roomInfo, type ArtEntry, type ArtLibrary, type ArtState, type RoomInfo } from '@lr3d/shared';
import { Interaction, interactiveName, setInteractiveName } from './interact/interaction';
import { Panel } from './ui/panel';
import { RoomVarsStore } from './room/vars';
import { applyProbe, captureProbe, loadEnvProbe, setProbeGeometry, type ProbeResult } from './render/reflections';
import { AdaptiveResolution, FrameStats, RefreshEstimator } from './render/perf';
import { GpuTimer } from './render/gpu-timer';
import { Input } from './player/input';
import { Player } from './player/player';
import { pickSpawn } from './player/spawn';
import { AudioEngine } from './audio/engine';
import { LocalSource, type MediaItem } from './audio/media';
import { VoyageSource } from './media/voyage/source';
import { PlayOrder } from './media/voyage/order';
import { ScreenPosters } from './video/posters';
import { ScreenVideo } from './video/screens';
import { makeMirror } from './render/mirror';
import type { Reflector } from 'three/examples/jsm/objects/Reflector.js';
import type { NetClient } from './net/client';
import { checkSession, initEditMode, leaveEditMode, lockedOut, onEditChange } from './net/editor';
import { canEditArt } from './art/permissions';
import { setEditRefused } from './art/upload';
import type { Avatars } from './net/avatars';
import { UI } from './ui/ui';
import type { Hud } from './ui/hud';

const TONE: Record<string, ToneMapping> = {
  none: NoToneMapping,
  linear: LinearToneMapping,
  reinhard: ReinhardToneMapping,
  aces: ACESFilmicToneMapping,
  agx: AgXToneMapping,
  neutral: NeutralToneMapping,
  'khronos_pbr_neutral': NeutralToneMapping,
};

const _size = new Vector2();
const _frustum = new Frustum();
const _m4 = new Matrix4();
const _sphere = new Sphere();
// transitions on once the styled page has been drawn (style.css html.preload; before UI, whose card fade-in starts
// in the same frame at the earliest)
requestAnimationFrame(() => requestAnimationFrame(() => document.documentElement.classList.remove('preload')));
const ui = new UI();
const panel = new Panel();
/** Debug HUD, loaded on demand (?debug or the backquote key). */
let hud: Hud | null = null;
const loadHud = () => import('./ui/hud').then(({ Hud }) => (hud ??= new Hud(true)));
if (config.debug) void loadHud();
/** Safari and every iOS browser: WebKit's audio thread is the one that stuttered. */
const ua = navigator.userAgent;
const webkit = /AppleWebKit/.test(ua) && !/Chrome\/|Chromium|Edg\/|Firefox\//.test(ua);
const canvas = document.getElementById('c') as HTMLCanvasElement;

/** Load timeline (ms since navigation start, and stage durations), for the HUD and benchmarks. */
const timings: Record<string, number> = {};
const tmark = (name: string, since?: number) => { const t = performance.now(); timings[name] = Math.round((since === undefined ? t : t - since) * 10) / 10; return t; };

/** The page's fill under the canvas (style.css body::before) in the room's background tone. */
const showRoomBackground = (m: Manifest) =>
  document.documentElement.style.setProperty('--room-bg', `#${backgroundOf(m).toString(16).padStart(6, '0')}`);

async function main(): Promise<void> {
  tmark('start');
  ui.progress(0.02, 'Loading scene…');
  const mediaList = loadMediaList();
  // rooms (contracts section 9): the room from the URL (?room=), its scene from rooms.json, the build from the
  // scene registry (channel live, or ?channel= / ?build= / ?scene= for the first room)
  const roomsFile = await loadRooms();
  // a room connection set (docs/plans/admin.md section 3): ?x=<slug> applies the set's doors to rooms.json for the
  // whole page (room switches keep x in the URL); the first room is the one the URL names, else the set's start
  const set = await loadExperience(config.serverHttp, roomsFile);
  const rooms = set?.ok ? set.rooms : roomsFile;
  if (set?.ok) useRooms(rooms);
  else if (set) history.replaceState(history.state, '', withoutExperience());
  const setMessage = set && !set.ok ? set.message : null;
  let roomId = set?.ok ? namedRoom(rooms) ?? set.experience.start : roomFromUrl(rooms);
  // a room of another site, asked for on a site's domain: load it on its own domain (docs/sites-and-domains.md)
  const canonical = crossSiteUrl(rooms, roomId, new URL(location.href));
  if (canonical) { location.replace(canonical); return; }
  /** The room at `/` on this domain (its site's default room). */
  const home = homeRoom(rooms);
  // with a set, the address bar names the room (/<start>?x=<slug>), so a reload or a copied link opens this room
  if (set?.ok) history.replaceState(history.state, '', urlForRoom(roomId, home, undefined, rooms));
  let room: RoomInfo = roomInfo(rooms, roomId)!;
  // pictures (contracts section 8): the room's art state loads alongside the scene; the slots once the scene id is known
  const artHttp = config.serverHttp;
  // edit mode (docs/plans/admin.md, net/editor.ts): the admin link's key leaves the address bar now, before anything
  // else reads or rewrites the URL; the exchange and the session check run alongside the loading
  const editStart = initEditMode(artHttp);
  setEditRefused(() => { if (artHttp) void checkSession(artHttp); });
  type ArtInitial = { art: ArtState; library?: ArtLibrary } | null;
  const fetchArt = (id: string): Promise<ArtInitial> => artHttp
    ? fetch(`${artHttp}/art/${encodeURIComponent(id)}`).then((r) => (r.ok ? r.json() : null)).then((j) => (j?.art ? { art: j.art as ArtState, library: j.library as ArtLibrary | undefined } : null)).catch(() => null)
    : Promise.resolve(null);
  const artInitial = fetchArt(roomId);
  const first = await loadRoomScene(roomId, { scene: config.scene, channel: config.channel, build: config.build });
  let manifest: Manifest = first.manifest;
  let base = first.base;
  let sceneKey = first.base; // the loaded build (rooms that share a build keep the scene on a switch)
  room = first.info;
  const loadSlots = (sceneId: string) => SlotSet.load(new URL(`art/slots/${sceneId}.json`, location.href).href).catch((e) => {
    console.info('[art] no hang slots for this scene', e?.message ?? e);
    return null;
  });
  const slotsP = loadSlots(manifest.scene_id);
  tmark('manifest');
  const tier = pickTier(manifest, config.tier);
  const mobile = tier === 'mobile';

  // ---------------------------------------------------------------- renderer
  // MSAA: on the desktop tier (Apple and recent desktop GPUs resolve it cheaply), off on mobile, where it
  // costs bandwidth on every phone GPU
  const antialias = !mobile;
  const renderer = new WebGLRenderer({
    canvas,
    antialias,
    powerPreference: 'high-performance',
    stencil: false,
    alpha: false,
    depth: true,
  });
  // pixel ratio: desktop starts at the native ratio up to 2; mobile starts at 1.25 and may rise to 1.5 (phones
  // are 2-3x; the baked textures carry the detail, and fill rate is what a mid-range phone runs out of first)
  const nativeDpr = window.devicePixelRatio || 1;
  const maxDpr = Math.min(nativeDpr, mobile ? 1.5 : 2);
  const minDpr = Math.max(0.5, Math.min(1, nativeDpr) * 0.6);
  const initialDpr = config.fixedDpr ?? (mobile ? Math.min(maxDpr, 1.25) : maxDpr);
  renderer.setPixelRatio(initialDpr);
  // live lighting (contract section 7)
  const shading = new RoomShading();
  // dither and 2-bit look (rooms.json look; docs/plans/dither-2bit.md)
  const retro = new RetroLook();
  retro.setLook(roomLook(rooms, roomId));
  renderer.toneMapping = TONE[(manifest.tone_mapping || 'none').toLowerCase()] ?? NoToneMapping;
  renderer.toneMappingExposure = manifest.exposure ?? 1;
  const roomVars = new RoomVarsStore();
  shading.apply(roomVars.values);
  panel.set(roomVars.values);
  const roomControls = () => { panel.setRoom(room.controls); setInteractiveName('room_controls', room.controls.title); };
  roomControls();
  renderer.shadowMap.enabled = false;
  canvas.addEventListener('webglcontextlost', (e) => e.preventDefault());
  canvas.addEventListener('webglcontextrestored', () => location.reload());

  const scene = new Scene();
  // the room's plaster tone, not black: a hairline gap between two surfaces then shows a wall-like colour
  scene.background = new Color(backgroundOf(manifest));
  showRoomBackground(manifest);
  scene.matrixWorldAutoUpdate = false; // everything static updates its own matrices
  const camera = new PerspectiveCamera(65, 1, 0.05, 60);
  scene.add(camera);

  // render loop state (the loop itself is below); invalidate() asks for a frame and wakes the loop
  let needsRender = true;
  let awake = false;
  let lingerUntil = 0;
  let frameFn: ((now: number) => void) | null = null;
  let prevT = performance.now();
  let prevRendered = false;
  /** Keep requesting animation frames for a while after the last activity (clean refresh-rate samples, last net send). */
  const LINGER_MS = 600;
  function wake(): void {
    const now = performance.now();
    lingerUntil = Math.max(lingerUntil, now + LINGER_MS);
    if (awake || !frameFn) return;
    awake = true;
    prevT = now;
    prevRendered = false;
    requestAnimationFrame(frameFn);
  }
  const invalidate = () => { needsRender = true; wake(); };

  let cssW = 1, cssH = 1;
  function resize(): void {
    const w = canvas.clientWidth || innerWidth, h = canvas.clientHeight || innerHeight;
    cssW = w; cssH = h;
    renderer.setSize(w, h, false);
    interaction?.resize(w, h);
    const aspect = w / h;
    camera.aspect = aspect;
    // keep at least ~74 deg horizontal on portrait screens, 62 deg vertical otherwise
    const minH = (74 * Math.PI) / 180;
    const vFromH = (2 * Math.atan(Math.tan(minH / 2) / aspect) * 180) / Math.PI;
    camera.fov = Math.min(95, Math.max(62, vFromH));
    camera.updateProjectionMatrix();
    adaptiveRef?.hold(performance.now(), 1000);
    gpu?.reset();
    // a new drawing buffer size clears the canvas: draw it again now, in the same task, or the browser shows the
    // cleared (black) canvas for a frame. That was the occasional one-frame black flash (2026-10-26): every change
    // of the adaptive pixel ratio (made inside the frame, after its render) and every window resize while the
    // on-demand loop was asleep.
    retro.render(renderer, scene, camera);
    invalidate();
  }
  let adaptiveRef: AdaptiveResolution | null = null;
  let gpu: GpuTimer | null = null;
  let interaction: Interaction | null = null;
  addEventListener('resize', resize);
  // Draw a new frame when the page comes back (tab or window switch, back/forward cache): the render loop
  // is on demand, so otherwise the compositor keeps showing the canvas layer it had before (NOTES.md, Page frame).
  document.addEventListener('visibilitychange', () => { if (!document.hidden) invalidate(); });
  addEventListener('focus', invalidate);
  addEventListener('pageshow', invalidate);
  resize();

  // ---------------------------------------------------------------- scene
  // Progressive on desktop: the mobile tier (a few MB) is shown first; the desktop tier loads behind it and
  // replaces it when ready. ?tier= forces one tier.
  const progressive = tier === 'desktop' && !config.tier && !!manifest.tiers.mobile && !manifest.outdoor; // a generated field: one tier
  const firstTier: TierName = progressive ? 'mobile' : tier;
  const reflectFor = (t: TierName) => t !== 'mobile'; // the reflection layer: desktop tier only
  /** The room's sound: the scene manifest's audio, with this room's rooms.json audio (reverb) over it. */
  const roomAudio = () => ({ ...manifest.audio, ...room.audio });
  let floorY = manifest.collision.floor_y;
  let shownTier: TierName = firstTier;
  let probe: ProbeResult | null = null;

  /**
   * Make a loaded tier ready to draw without hitches: upload its textures one at a time (yielding in between, so
   * no single task uploads ~100 MB), capture its reflection probe (in a scene of its own) and compile its programs.
   */
  async function prepareTier(s: LoadedScene, t: TierName): Promise<ProbeResult | null> {
    shading.prepare(s); // patch the materials before their programs compile (below)
    retro.prepare(s);
    for (const tex of s.textures) {
      renderer.initTexture(tex);
      await new Promise((r) => setTimeout(r, 0));
    }
    releaseTextureData(s);
    performance.mark(`lr3d:${t}:uploaded`);
    const own = new Scene();
    own.background = scene.background;
    own.matrixWorldAutoUpdate = false;
    own.add(s.root);
    let p: ProbeResult | null = null;
    if (s.reflective.length) {
      const size = t === 'mobile' ? 128 : 256;
      const bounds = new Box3().setFromObject(s.root);
      const box = new Box3();
      for (const [x, z] of manifest.collision.walkable) box.expandByPoint(new Vector3(x, floorY, z));
      box.min.y = floorY;
      box.max.y = bounds.max.y;
      const pp = manifest.reflection?.probe_position;
      const probePos = pp ? new Vector3(pp[0], pp[1], pp[2]) : box.getCenter(new Vector3());
      const env = manifest.reflection?.env_url;
      try {
        p = env ? await loadEnvProbe(renderer, base + env, size) : captureProbe(renderer, own, probePos, size, false);
      } catch (e) {
        console.warn('[reflect] env probe failed, capturing instead', e);
        p = captureProbe(renderer, own, probePos, size, false);
      }
      setProbeGeometry(box, probePos, size);
      applyProbe(s.reflective, p.texture);
    }
    performance.mark(`lr3d:${t}:probe`);
    try {
      await renderer.compileAsync(own, camera);
    } catch {
      renderer.compile(own, camera);
    }
    return p;
  }

  ui.progress(0.05, `Loading ${manifest.scene_id} (${firstTier})…`);
  let loaded: LoadedScene;
  // a scene's per-render step (outdoor field: one-off meshes moved to their copy nearest the camera), before three's
  // frustum culling; every render of the room scene runs it
  scene.onBeforeRender = (_r, _s, cam) => loaded?.beforeRender?.(cam);
  try {
    loaded = await loadScene(renderer, manifest, base, firstTier, (f) => ui.progress(0.05 + f * 0.8), { reflect: reflectFor(firstTier), shapes: shapeNodes(manifest, rooms) });
  } catch (e) {
    throw new Error(`Could not load ${base}${manifest.tiers[firstTier]?.url}: ${(e as Error).message}`);
  }
  Object.assign(timings, { fetch: Math.round(loaded.timings.fetch), parse: Math.round(loaded.timings.parse), prepare: Math.round(loaded.timings.prepare) });
  const tLoaded = tmark('loaded');
  ui.progress(0.88, 'Uploading textures…');
  probe = await prepareTier(loaded, firstTier);
  scene.add(loaded.root);
  tmark('upload+probe+compile', tLoaded);
  shading.setAccentPoint(accentPoint(loaded));
  // pictures take their light from the wall behind them: the slots are sampled once the room is in the scene
  let slots = await slotsP;
  // `?c=1`: art files cached before the server sent CORS headers on every answer (2026-10-24) are blocked as
  // textures and as crossorigin <img>; a new address makes every browser fetch them again. The server ignores it.
  const fileUrl = (e: ArtEntry) => `${artHttp}${e.url}?c=1`;
  // surface slots (the gallery's window view and oval) only where the build has their mesh; the oval takes no picture
  // in a room where it is a mirror
  const viewSlots = (set: SlotSet | null) => {
    if (!set) return;
    const nodes = new Set<string>();
    scene.traverse((o) => { if (o.userData.screenNode) nodes.add(o.userData.screenNode as string); });
    for (const [kind, sp] of Object.entries(SURFACES)) {
      const mirrored = (manifest.screens ?? []).some((x) => x.node === sp.node && room.mirrors.includes(x.id));
      if (!nodes.has(sp.node) || mirrored) set.removeKind(kind);
    }
    // the overlay slot only in a room with an overlay setting (docs/plans/basement-overlay.md)
    if (!room.overlay) set.removeKind(OVERLAY_KIND);
  };
  viewSlots(slots);
  let artworks = slots ? new Artworks(renderer, scene, shading, slots, fileUrl) : null;
  // the Basement overlay: a picture over the view near the room's centre, and its brown noise (per visitor)
  const overlay = new ScreenOverlay({ mobile });
  overlay.setConfig(room.overlay);
  artworks?.init();
  artworks?.animate({ mobile, invalidate }); // animated pictures: canvas sizes, memory and fps cap per tier; renders on new frames

  // ---------------------------------------------------------------- interaction, panel, room variables
  let artPanel: ArtPanel | null = null;
  /** Interactive ids that only select (hover label, outline) and open nothing. */
  const SELECT_ONLY = new Set(['skull', 'globe']);
  /** Open the panel for an interactive object or a hang slot (one panel at a time). */
  const openPanel = (id: string) => {
    if (id.startsWith(PORTAL_PREFIX)) {
      const p = portals.byKey(id);
      if (p) void goThrough(p);
      return;
    }
    if (slots?.get(id)) {
      panel.close();
      artPanel?.open(id);
    } else if (SELECT_ONLY.has(id)) {
      // selectable, no panel yet (the gallery's vanitas globe, earlier the skull: a dialog comes later)
    } else {
      artPanel?.close();
      panel.open(interactiveName(id));
    }
  };
  const anyPanelOpen = () => panel.isOpen || !!artPanel?.isOpen;
  const closePanels = () => { panel.close(); artPanel?.close(); };
  interaction = new Interaction(scene, camera, canvas, {
    open: openPanel,
    empty: () => {
      if (!anyPanelOpen()) return false;
      closePanels();
      return true;
    },
    invalidate: () => invalidate(),
  });
  interaction.resize(cssW, cssH);
  const ia = interaction;
  // portals (contracts section 9): doors to other rooms, interactive like the room's objects
  const portals = new Portals(scene);
  const urlFlags = new URLSearchParams(location.search);
  const flagOn = (f: string) => ['1', 'true', 'on', ''].includes(urlFlags.get(f) ?? 'x');
  function setInteractive(s: LoadedScene): void {
    portals.set(manifest, room, flagOn);
    for (const p of portals.list) setInteractiveName(p.key, `To ${roomInfo(rooms, p.link.to_room)?.title ?? p.link.to_room}`);
    ia.setScene(new Map([...s.interactive, ...portals.interactive()]), portals.shapes(s.shapes));
  }
  setInteractive(loaded);
  /** A room switch is running (portals, back button). */
  let switching = false;
  let net: NetClient | null = null;
  let outline: SlotOutline | null = null;
  /** The picture panel's view of the current room (one panel for every room; its slots follow the room). */
  const artCtx = {
    get slots() { return slots!; },
    get artworks() { return artworks!; },
    writer: () => {
      const token = net?.authToken;
      return artHttp && token ? { base: artHttp, room: roomId, token } : null;
    },
    fileUrl,
  };
  /** Pictures of the current room: hang slots as pickable surfaces, the outline, the panel (created once). */
  function setupArt(initial: Promise<ArtInitial>): void {
    const sl = slots, aw = artworks;
    if (!sl || !aw) { ia.setSurfaces(null); outline = null; return; }
    const out = (outline = new SlotOutline(scene, ia, sl, aw));
    ia.setSurfaces({
      // empty slots only for those who may add a picture (the edit lock, permissions.ts)
      pick: (ray) => {
        const hit = sl.pick(ray, (id) => aw.has(id));
        return hit && !aw.has(hit.id) && !canEditArt(roomId, hit.id) ? null : hit;
      },
      owns: (id) => !!sl.get(id),
      label: () => '', // hovering a slot changes the cursor only
      showSelected: (id) => {
        // the window view gets no outline (it would cover the view it selects)
        out.show(id && sl.get(id) && !frameless(sl.get(id)!.kind) ? id : null);
        if (!(id && sl.get(id))) artPanel?.close();
        invalidate();
      },
    });
    if (!artPanel) {
      const ap = (artPanel = new ArtPanel(artCtx));
      ap.onClose = () => canvas.focus({ preventScroll: true });
      ap.onSelectSlot = (id) => { ia.select(id); ap.open(id); };
      ap.onApplied = (slot, art) => artworks?.set(slot, art);
    }
    const ap = artPanel;
    // a picture in the oval replaces the music poster there; an empty oval shows the poster again
    aw.onSurface = (_kind, mesh, hung) => {
      const isPoster = (manifest.screens ?? []).some((x) => x.node === mesh.userData.screenNode && isPosterScreen(x));
      if (hung) posterMeshes = posterMeshes.filter((m) => m !== mesh);
      else if (isPoster && !posterMeshes.includes(mesh)) posterMeshes = [...posterMeshes, mesh];
      posters.setMeshes(posterMeshes);
    };
    // the overlay slot's picture goes over the view (overlay.ts), not into the scene
    aw.onOutside = (slot, e) => { if (slot === room.overlay?.slot) overlay.setImage(e ? fileUrl(e) : null, e?.hash); };
    overlay.onShown = (hash, failed) => { const id = room.overlay?.slot; if (id && artworks === aw) aw.outsideShown(id, hash, failed); };
    aw.onChange = (slot) => {
      // a slot under a picture that just went up can no longer be selected
      const sel = ia.selected;
      if (sel && sl.get(sel) && sl.get(sel)!.coveredBy.some((c) => aw.has(c))) {
        ia.select(null);
        ap.close();
      } else if (out.shown === slot || (sel && sl.conflicts(slot).includes(sel))) out.show(ia.selected);
      ap.refresh();
      ia.refreshLabel();
      invalidate();
    };
    ap.applyLibrary({}); // the previous room's libraries go; this room's come with the state below or the welcome
    void initial.then((state) => {
      if (!state || artworks !== aw) return;
      aw.applyState(state.art);
      ap.applyLibrary(state.library ?? {}); // also clears the previous room's libraries
    });
  }
  setupArt(artInitial);
  roomVars.onChange = (v) => {
    shading.apply(v); // uniforms only
    panel.set(v);
    invalidate();
  };
  panel.onInput = (patch) => roomVars.set(patch);
  panel.onReset = () => roomVars.reset();
  panel.onClose = () => canvas.focus({ preventScroll: true });
  // edit mode: lights locked for visitors (decision 1) stay in their browser; pictures read only (permissions.ts)
  const panelNote = document.querySelector<HTMLElement>('#panel .panel-note');
  const sharedNote = panelNote?.textContent ?? '';
  const applyEditMode = () => {
    roomVars.local = lockedOut('vars');
    if (panelNote) panelNote.textContent = roomVars.local ? 'Changes here are only in your browser; the room\'s editors set the lights for everyone.' : sharedNote;
    net?.sendAuth();
    // an empty slot selected before the lock was known: visitors cannot select empty slots
    const sel = ia.selected;
    if (sel && slots?.get(sel) && !artworks?.has(sel) && !canEditArt(roomId, sel)) { ia.select(null); artPanel?.close(); }
    artPanel?.refresh();
    invalidate();
  };
  onEditChange(applyEditMode);
  applyEditMode();
  void editStart.then((msg) => { if (msg) ui.notice(msg, 6000); });
  if (setMessage) ui.notice(setMessage, 6000);

  // ---------------------------------------------------------------- player, input
  let player = new Player(manifest);
  player.apply(camera);
  // arrival from another site through a door (room/arrival.ts): at the arrival door, facing in, as after a switch
  let firstArrive: { x: number; z: number; yaw: number } | null = null;
  if (arrival?.spawn || arrival?.from) {
    const from = arrival.from;
    const id = arrival.spawn ?? Object.entries(room.portals).find(([, l]) => l.to_room === from && !l.closed)?.[0]
      ?? manifest.portals?.find((x) => x.to_room === from && !room.portals[x.id])?.id;
    const a = portalSpawn(manifest, id, room);
    if (a) {
      player.collider.settle(a.x, a.z);
      player.x = player.collider.x; player.z = player.collider.z; player.yaw = a.yaw;
      player.apply(camera);
      portals.disarmAt(player.x, player.z);
      firstArrive = { x: player.x, z: player.z, yaw: player.yaw };
    }
  }
  const input = new Input(canvas);

  // screens: the monitor shows posters (contract section 5), every other screen shows the item's video
  // poster screens: the monitor, and screens whose manifest entry says content "posters" (the gallery's looking glass,
  // front and back: the same poster on both for now). The canvas aspect comes from the monitor, else the first one.
  const isPosterScreen = (s: { id: string; content?: string }) => s.id === 'monitor' || s.content === 'posters';
  const posterInfoOf = () => (manifest.screens ?? []).find((s) => s.id === 'monitor') ?? (manifest.screens ?? []).find(isPosterScreen);
  let monitorInfo = posterInfoOf();
  let screenSpheres: Sphere[] = [];
  let posterSpheres: Sphere[] = [];
  let mirrors: Reflector[] = [];
  /** The meshes the poster screens draw on now (screen meshes less surfaces with a picture). */
  let posterMeshes: Mesh[] = [];
  function screenMeshesOf(s: LoadedScene): { video: Mesh[]; posters: Mesh[] } {
    monitorInfo = posterInfoOf();
    const out = { video: [] as Mesh[], posters: [] as Mesh[] };
    for (const r of mirrors) { r.removeFromParent(); r.dispose(); }
    mirrors = [];
    for (const info of manifest.screens ?? []) {
      const m = s.screens.get(info.node);
      if (!m) { console.warn(`[video] screen node ${info.node} not found in the glb`); continue; }
      // the gallery's window view: the art system puts the room's chosen image on it (art/artworks.ts, slot kind view)
      if (info.content === 'view' || info.node === 'window_view') continue;
      // a mirror in this room (rooms.json `mirrors`; desktop only): a planar reflection instead of the poster
      if (!mobile && room.mirrors.includes(info.id)) {
        const r = makeMirror(m, info, s.root);
        if (r) { mirrors.push(r); continue; }
      }
      // a surface with a picture hung (the oval) is not a poster screen until the picture goes
      if (isPosterScreen(info) && artworks && [...artworks.slots.slots.values()].some((sl) => SURFACES[sl.kind]?.node === info.node && artworks!.has(sl.id))) continue;
      (isPosterScreen(info) ? out.posters : out.video).push(m);
    }
    posterMeshes = out.posters;
    // a screen redraws the room on video frames only while it is in the view frustum
    const sphereOf = (m: Mesh) => {
      m.geometry.computeBoundingSphere();
      return m.geometry.boundingSphere!.clone().applyMatrix4(m.matrixWorld);
    };
    screenSpheres = out.video.map(sphereOf);
    posterSpheres = out.posters.map(sphereOf); // the poster animation draws only while the monitor is in view
    return out;
  }
  const screenMeshes = screenMeshesOf(loaded);
  const anyVisible = (spheres: Sphere[]) => {
    _frustum.setFromProjectionMatrix(_m4.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse));
    for (const sp of spheres) if (_frustum.intersectsSphere(_sphere.copy(sp))) return true;
    return false;
  };
  const screensVisible = () => anyVisible(screenSpheres);
  // the letter on a static screen face: the manifest screen's `letter` (the concrete room's round screen shows B)
  const screenLetter = (m: Mesh) => (manifest.screens ?? []).find((x) => x.node === m.userData.screenNode)?.letter ?? 'A';
  const video = new ScreenVideo(screenMeshes.video, invalidate, screensVisible, screenLetter);
  const posters = new ScreenPosters(screenMeshes.posters, monitorInfo, mobile, invalidate, () => anyVisible(posterSpheres));
  // the monitor's height on screen in device pixels (picks the size of the poster / video canvas)
  posters.viewHeightPx = () => {
    const sp = posterSpheres[0];
    if (!sp || !monitorInfo?.height) return Infinity;
    const d = Math.max(0.1, camera.position.distanceTo(sp.center));
    return (monitorInfo.height / (2 * d * Math.tan((camera.fov * Math.PI) / 360))) * renderer.domElement.height;
  };

  /** Load the desktop tier behind the mobile one and swap it in (progressive loading). */
  async function upgradeTier(): Promise<void> {
    const t0 = performance.now();
    const key = sceneKey;
    try {
      const hi = await loadScene(renderer, manifest, base, 'desktop', () => {}, { reflect: reflectFor('desktop'), background: true, shapes: shapeNodes(manifest, rooms) });
      if (key !== sceneKey) { disposeScene(hi); return; } // the room changed meanwhile
      const hiProbe = await prepareTier(hi, 'desktop');
      if (key !== sceneKey) { disposeScene(hi); hiProbe?.dispose(); return; }
      const old = loaded, oldProbe = probe;
      scene.add(hi.root);
      old.root.removeFromParent();
      loaded = hi;
      probe = hiProbe;
      shownTier = 'desktop';
      const m = screenMeshesOf(hi);
      video.setMeshes(m.video);
      posters.setMeshes(m.posters);
      setInteractive(hi);
      artworks?.reapplySurfaces(); // the window view's and the oval's pictures onto the new tier's meshes
      disposeScene(old);
      oldProbe?.dispose();
      tmark('upgrade', t0);
      tmark('upgradedAt');
      invalidate();
    } catch (e) {
      console.warn('[scene] the desktop tier failed to load; staying on the mobile tier', e);
    }
  }
  let avatars: Avatars | null = null;


  // ---------------------------------------------------------------- loop
  const stats = new FrameStats();
  const refresh = new RefreshEstimator();
  gpu = new GpuTimer(renderer.getContext());
  const adaptive = new AdaptiveResolution({
    min: minDpr,
    max: maxDpr,
    initial: initialDpr,
    onChange: (dpr) => { renderer.setPixelRatio(dpr); resize(); },
  });
  adaptive.enabled = config.fixedDpr === null;
  adaptiveRef = adaptive;
  adaptive.hold(performance.now(), 3000);

  let audio: AudioEngine | null = null;
  let entered = false;
  let items: MediaItem[] = [];
  let order = new PlayOrder(0, false); // play order over `items` (voya.ge's rules, media/voyage/order.ts)
  let movedFor = 0; // seconds of movement since entering (dismisses the controls hint)
  const pose = { x: 0, y: 0, z: 0, yaw: 0, pitch: 0 };
  const getPose = () => {
    pose.x = player.x; pose.y = player.floorY; pose.z = player.z; pose.yaw = player.yaw; pose.pitch = player.pitch;
    return pose;
  };

  /**
   * Render on demand. The loop runs only while something changes (input, movement, avatars interpolating, a
   * pending listener update, video frames of a visible screen, invalidate()) and LINGER_MS after that; then it
   * stops requesting animation frames, so an idle room costs no CPU or GPU time.
   */
  function frame(now: number): void {
    const interval = now - prevT;
    prevT = now;
    // refresh rate from frames without rendering (a loaded frame says nothing about the display), or from
    // frames that came faster than the current estimate
    if (!prevRendered || interval < refresh.ms * 0.9) refresh.add(interval);
    const dt = Math.min(0.05, Math.max(0, interval / 1000));
    const t0 = performance.now();
    let changed = false;
    let active = false;
    if (entered && player.update(dt, input)) {
      player.apply(camera);
      audio?.updateListener(camera);
      ia.cameraMoved();
      changed = true;
      if (ui.hintActive && (movedFor += dt) > 3) ui.dismissHint();
    }
    if (entered && (player.moving || input.moving)) active = true;
    // the Basement overlay and its noise follow this player's distance from the room's centre, gliding. Not during a
    // room switch: the new room's overlay is set before the player is placed, so the old position would give a false
    // distance and start the noise (a burst on arriving in the hallway, 2026-10-30).
    if (!switching && (overlay.active || overlay.opacity > 0 || (audio?.overlayNoise?.target ?? 0) > 0)) {
      if (overlay.update(player.x, player.z, dt)) active = true;
      if (entered) audio?.setOverlayNoise((overlay.active?.noise_gain ?? 0) * overlay.amount, overlay.active?.glide_s ?? 0.45);
      if (audio?.overlayNoise && Math.abs(audio.overlayNoise.target - (overlay.active?.noise_gain ?? 0) * overlay.amount) > 1e-5) active = true;
    }
    // walking into a door (portal) switches rooms
    if (entered && !switching && portals.list.length && player.moving) {
      const hit = portals.check(player.x, player.z, player.vx, player.vz);
      if (hit) void goThrough(hit);
    }
    if (audio) {
      audio.flushListener();
      if (audio.listenerPending) active = true;
    }
    if (net) {
      net.tick(now);
      if (avatars && avatars.count) {
        const rt = net.renderTime();
        if (avatars.update(rt, floorY, player.x, player.z, player.wrap)) changed = true;
        if (net.interpolating(rt)) active = true;
      }
    }
    if (video.needsContinuousRender) changed = true;
    // an animated dither (rooms.json look.dither_fps) draws a new pattern at its own rate
    if (retro.animated) {
      active = true;
      if (retro.tick(now)) changed = true;
    }
    ia.frame(); // a pending hover raycast (pointer moved or camera moved), at most once per frame
    const render = changed || needsRender || config.bench;
    if (render) {
      needsRender = false;
      gpu!.poll(now);
      gpu!.begin();
      retro.render(renderer, scene, camera);
      gpu!.end();
      if (avatars && avatars.count) avatars.updateLabels(camera, cssW, cssH);
      const cpu = performance.now() - t0;
      stats.rendered(now, interval, cpu);
      if (timings.firstFrame === undefined) { tmark('firstFrame', t0); tmark('firstFrameAt'); }
      adaptive.frame(now, interval, true, prevRendered, refresh.ms, cpu, gpu!.ms);
    } else {
      adaptive.frame(now, interval, false, prevRendered, refresh.ms, 0, NaN);
    }
    prevRendered = render;
    if (hud?.visible) {
      active = true;
      if (hud.due(now)) showHud(now);
    }
    if (active || render || config.bench) lingerUntil = Math.max(lingerUntil, now + LINGER_MS);
    if (now < lingerUntil || config.bench) requestAnimationFrame(frame);
    else {
      awake = false;
      gpu!.poll(now);
    }
  }

  function showHud(now: number): void {
    if (!hud) return;
    const info = renderer.info;
    const size = renderer.getDrawingBufferSize(_size);
    hud.show(now, {
      fps: stats.fps(now),
      frameMs: stats.meanInterval(30),
      cpuMs: stats.meanCpu(30),
      gpuMs: gpu!.available ? gpu!.ms : NaN,
      refreshHz: Math.round(1000 / refresh.ms),
      calls: info.render.calls,
      triangles: info.render.triangles,
      textureBytes: loaded.textureBytes + (probe?.bytes ?? 0) + video.textureBytes + posters.textureBytes + (artworks?.stats.textureBytes ?? 0),
      textures: info.memory.textures,
      programs: info.programs?.length ?? 0,
      tier: shownTier === tier ? tier : `${shownTier} (${tier} loading)`,
      dpr: renderer.getPixelRatio(),
      size: `${size.x}x${size.y}`,
      features: `aa ${antialias ? 'on' : 'off'} · reflect ${loaded.reflective.length ? 'on' : 'off'} · pan ${audio?.opts.panning ?? '-'}${audio?.opts.reverb ? '+rev' : ''} · dpr -${adaptive.stats.lowered} +${adaptive.stats.raised} cpu-bound ${adaptive.stats.cpuBound}`,
      load: `ready ${Math.round(timings.ready ?? 0)} ms (fetch ${timings.fetch} parse ${timings.parse} gpu prep ${timings['upload+probe+compile']})${timings.upgrade ? ` · desktop tier +${Math.round(timings.upgrade)} ms` : ''}`,
      scene: `${manifest.scene_id}${manifest.variant ? `/${manifest.variant}` : ''}`,
      net: net ? `${net.status}${net.status === 'online' ? ` peers ${net.peers.size} rtt ${Math.round(net.clock.rtt)} ms` : ''} id ${net.id ?? '-'} conn ${net.stats.connects} resumed ${net.stats.resumed}` : 'not started',
      rendering: stats.fps(now) > 0,
      pos: `${player.x.toFixed(2)} ${player.z.toFixed(2)} yaw ${((player.yaw * 180) / Math.PI).toFixed(0)}`,
    });
  }

  frameFn = frame;
  input.onActivity = wake;
  wake();

  // ---------------------------------------------------------------- media
  function setUiMedia(): void {
    ui.media(audio?.playing ?? false);
    posters.setPlaying(audio?.playing ?? false);
  }

  // Playback is per listener (not shared through the server): play/pause/next affect only this client.
  let selectSeq = 0;
  /** Make `item` current: a different item is loaded (the old one fades out, the bridge comes up), its poster and video follow. */
  async function selectItem(item: MediaItem, play: boolean): Promise<void> {
    if (!audio) return;
    const seq = ++selectSeq;
    if (audio.item?.id !== item.id) {
      posters.set(item.posters?.length ? item.posters : item.poster ? [item.poster] : [], item.video ?? null);
      video.set(item.video ?? null);
      setUiMedia();
      await audio.load(item);
      if (seq !== selectSeq) return;
    }
    if (play) await audio.play();
    else audio.pause();
    if (seq !== selectSeq) return;
    video.sync(audio.position, audio.playing);
    setUiMedia();
  }

  async function togglePlay(): Promise<void> {
    if (!audio || !audio.item) return;
    if (audio.playing) audio.pause(); else await audio.play();
    video.sync(audio.position, audio.playing);
    setUiMedia();
  }

  /** Next item, shuffled or in list order as on voya.ge (order.ts); a one-item list starts again from the beginning. */
  async function next(): Promise<void> {
    if (!audio || !items.length) return;
    const item = items[order.next()]!;
    if (item.id === audio.item?.id) {
      audio.restart();
      video.sync(audio.position, audio.playing);
      return;
    }
    await selectItem(item, true);
  }

  function onEnded(item: MediaItem): void {
    if (!audio || audio.item?.id !== item.id) return;
    void next();
  }

  ui.onPlayPause = () => void togglePlay();

  // 1 Hz: keep the ambient track at the primary's clock (audio engine) and the screen video at the audio clock
  setInterval(() => {
    if (!audio) return;
    audio.correctDrift();
    video.sync(audio.position, audio.playing);
  }, 1000);

  // ---------------------------------------------------------------- keys, clicks, hover
  input.onKey = (code) => {
    if (code === 'Escape') {
      // the panel first, then the selection
      if (anyPanelOpen()) closePanels();
      else ia.select(null);
      return;
    }
    if (code === 'Enter' && entered && ia.selected && !anyPanelOpen()) {
      openPanel(ia.selected);
      return;
    }
    if (code === 'Backquote') {
      if (hud) hud.toggle();
      else void loadHud();
      invalidate();
    }
    if (!entered) return;
    if (code === 'Space' || code === 'KeyP') void togglePlay();
    if (code === 'KeyN') void next(); // hidden shortcut (no next button on screen)
  };
  // Shift tap: open / close the room controls directly (no selection needed). Focus moves into the panel on
  // open (panel.open) and back to the canvas on close (panel.onClose), as with a click and the close button.
  input.onShiftTap = () => {
    if (!entered) return;
    if (panel.isOpen) panel.close();
    else { artPanel?.close(); panel.open(); } // the picture panel gives way to the room controls
  };
  input.onClick = (x, y, touch) => { if (entered) ia.click(x, y, touch); };
  input.onHover = (x, y) => { if (entered) ia.pointerMove(x, y); };
  input.onHoverEnd = () => ia.pointerLeave();
  input.onDrag = (d) => ia.setDragging(d);

  // ---------------------------------------------------------------- enter
  tmark('ready');
  ui.ready(`${manifest.scene_id} · ${tier} · ${(loaded.triangles / 1000).toFixed(1)}k triangles`);
  if (progressive) void upgradeTier();
  // Creating the AudioContext takes ~130 ms (the audio device is opened). Do it while the start card is up, not
  // in the Enter click; it starts suspended and resume() runs inside the click.
  let preAudio: AudioEngine | null = null;
  const audioOpts = { panning: (mobile || webkit ? 'equalpower' : 'HRTF') as PanningModelType, reverb: !mobile };
  const makeAudio = () => preAudio ?? (preAudio = new AudioEngine(manifest, audioOpts, roomAudio()));
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
  if (idle) idle(() => makeAudio(), { timeout: 1500 });
  else setTimeout(makeAudio, 300);
  ui.onEnter = () => {
    if (entered) return;
    entered = true;
    input.enabled = true;
    ui.hideOverlay();
    adaptive.hold(performance.now(), 1500);
    ui.hint();
    // the name field or the Enter button must not keep focus (keys are read on window, but a focused
    // input swallows them)
    (document.activeElement as HTMLElement | null)?.blur?.();
    canvas.focus({ preventScroll: true });
    // audio must start inside the gesture
    audio = makeAudio();
    void audio.resume();
    audio.updateListener(camera);
    audio.onChange = setUiMedia;
    audio.onEnded = onEnded;
    posters.clock = () => audio?.position ?? 0; // the monitor's video follows the local audio clock
    void (async () => {
      // multiplayer code loads after entering (not needed for the first paint)
      const netModules = Promise.all([import('./net/client'), import('./net/avatars')]);
      netModules.catch(() => {}); // awaited below; a failure there is reported once
      try {
        const list = await mediaList;
        items = list.items;
        order = new PlayOrder(items.length, list.shuffle);
        // arrival from another site: the music item and position it was at there (the start item, as voya.ge's
        // ?composition=<id>); otherwise the playlist's first item, or a random one when it shuffles
        const media = arrival?.media;
        const carried = media ? items.find((i) => i.id === media) : undefined;
        const first = items[order.first(carried ? items.indexOf(carried) : -1)];
        if (first) await selectItem(first, true);
        if (carried && arrival?.t && audio?.item?.id === carried.id) {
          audio.seek(arrival.t);
          video.sync(audio.position, audio.playing);
        }
      } catch (e) {
        console.error('[media] the music did not start', e); // the room still connects
      }
      const [{ NetClient }, { Avatars }] = await netModules;
      NetCtor = NetClient;
      avatars = new Avatars(scene, manifest.player.eye_height);
      connectNet(firstArrive);
    })().catch((e) => {
      // e.g. the multiplayer chunk of a client version that a deploy replaced while this page was open
      console.error('[net] the multiplayer code did not load', e);
      ui.dismissHint();
      ui.notice('The multiplayer part of the page did not load. Reload the page to join the room.', 15000);
    });
  };

  /** Join the current room (roomId) on the server. Arrival through a portal: `spawn` = where the player arrived. */
  let NetCtor: typeof NetClient | null = null;
  function connectNet(arrivedAt: { x: number; z: number; yaw: number } | null = null): void {
    if (!NetCtor || !avatars) return;
    const av = avatars;
    const n = (net = new NetCtor(config.server, roomId, ui.name, ui.color, {
      status: (s) => { if (net !== n) return; ui.net(s, n.peers.size); artPanel?.refresh(); },
      snapshot: wake,
      join: (p) => { av.add(p); if (net === n) ui.net(n.status, n.peers.size); invalidate(); },
      leave: (id) => { av.remove(id); if (net === n) ui.net(n.status, n.peers.size); invalidate(); },
      welcome: (w) => {
        ui.net('online', n.peers.size);
        // first join: take a free slot on the spawn arc (the room's spawn, or the portal we came through), unless
        // we already walked away from it
        if (!w.first) return;
        const sx = arrivedAt ? arrivedAt.x : manifest.spawn.position[0];
        const sz = arrivedAt ? arrivedAt.z : manifest.spawn.position[2];
        const syaw = arrivedAt ? arrivedAt.yaw : (manifest.spawn.yaw_deg * Math.PI) / 180;
        player.collider.settle(sx, sz);
        if (Math.hypot(player.x - player.collider.x, player.z - player.collider.z) > 0.3) return;
        const spot = pickSpawn(player.collider, sx, sz, syaw, w.peers.map((p) => ({ x: p.p[0], z: p.p[2] })));
        if (!spot) return;
        player.x = spot.x; player.z = spot.z; player.yaw = spot.yaw; player.vx = player.vz = 0;
        player.apply(camera);
        audio?.updateListener(camera, true);
        n.flushPose();
        invalidate();
      },
      vars: (v, info) => { if (net === n) roomVars.receive(v, info.full); },
      artState: (art) => { if (net !== n) return; artworks?.applyState(art); artPanel?.refresh(); },
      art: (slot, art) => { if (net !== n) return; artworks?.set(slot, artPanel ? artPanel.incoming(slot, art) : art); artPanel?.refresh(); },
      libraryState: (lib) => { if (net === n) artPanel?.applyLibrary(lib); },
      library: (wall, items) => { if (net === n) artPanel?.setLibrary(wall, items); },
    }, getPose));
    roomVars.send = (patch) => n.sendVars(patch);
    n.start();
  }

  // ---------------------------------------------------------------- rooms: going through a portal
  const fade = document.createElement('div');
  fade.id = 'room-fade';
  fade.style.cssText = 'position:fixed;inset:0;background:#0b0b0c;opacity:0;pointer-events:none;transition:opacity 280ms ease;z-index:40';
  document.body.appendChild(fade);
  const fadeTo = (o: number) => new Promise<void>((res) => {
    fade.style.pointerEvents = o > 0 ? 'auto' : 'none';
    if (getComputedStyle(fade).opacity === String(o)) { res(); return; }
    const done = () => { clearTimeout(t); fade.removeEventListener('transitionend', done); res(); };
    const t = setTimeout(done, 450);
    fade.addEventListener('transitionend', done);
    requestAnimationFrame(() => { fade.style.opacity = String(o); });
  });

  async function goThrough(p: ActivePortal): Promise<void> {
    // a door to a room of another site: a page load on that site's domain (docs/sites-and-domains.md), carrying the
    // arrival door, name, colour and music position
    const away = crossSiteUrl(rooms, p.link.to_room, new URL(location.href), {
      from: roomId, spawn: p.link.to_spawn, name: ui.name, color: ui.color, media: audio?.item?.id, t: audio?.position,
    });
    if (away) {
      if (switching) return;
      switching = true;
      closePanels();
      await fadeTo(1);
      net?.close();
      location.assign(away);
      return;
    }
    await switchRoom(p.link.to_room, p.link.to_spawn ?? null, true);
  }

  /**
   * Switch to another room in place (contracts section 9): fade out, leave the room on the server (bye), load the
   * other room's scene (the old one is disposed first: one scene in memory), place the player at the arrival portal
   * (or the scene's spawn) facing into the room, join the new room, fade in. The music keeps playing; the speakers
   * move to the new scene's. `push`: add a history entry (the back button switches back).
   */
  async function switchRoom(target: string, spawnId: string | null, push: boolean, over: SceneChoice = {}): Promise<void> {
    const info = roomInfo(rooms, target);
    if (switching || !info || target === roomId) return;
    switching = true;
    const t0 = performance.now();
    /** Set once the player has left the current room (before that, a failure keeps them in it). */
    let left = false;
    try {
      closePanels();
      ia.select(null);
      ia.clearHover(); // the portal's label and outline go now, not when the pointer next moves
      audio?.setOverlayNoise(0, 0.1); // the overlay's noise fades with the picture; the new room's starts from 0
      await fadeTo(1);
      const artP = fetchArt(target);
      // the target's manifest first: a room whose scene is not built (yet) leaves the player where they are
      const next: RoomScene = await loadRoomScene(target, over);
      left = true;
      net?.close();
      net = null;
      roomVars.send = () => {};
      const from = roomId;
      roomId = target;
      room = next.info;
      retro.setLook(roomLook(rooms, target), next.base === sceneKey ? loaded : undefined);
      roomControls();
      if (push) history.pushState({ room: target }, '', urlForRoom(target, home, undefined, rooms));
      // pictures: the old room's go (also when the scene stays: the new room has its own)
      outline?.dispose();
      outline = null;
      artworks?.dispose();
      artworks = null;
      // the overlay fades out (another room's image, or none); the new room's comes with its art state
      overlay.setConfig(room.overlay);
      overlay.setImage(null);
      if (next.base !== sceneKey) {
        // another build: dispose the old scene before loading the new one (one scene in memory, for phones)
        sceneKey = next.base;
        portals.clear();
        ia.setScene(new Map());
        disposeScene(loaded);
        probe?.dispose();
        probe = null;
        manifest = next.manifest;
        base = next.base;
        (scene.background as Color).setHex(backgroundOf(manifest));
        showRoomBackground(manifest);
        floorY = manifest.collision.floor_y;
        const t = config.tier && manifest.tiers[config.tier] ? config.tier : (manifest.tiers[tier] ? tier : (Object.keys(manifest.tiers)[0] as TierName));
        ui.progress(0.1, `Loading ${room.title}…`);
        const s = await loadScene(renderer, manifest, base, t, () => {}, { reflect: reflectFor(t), shapes: shapeNodes(manifest, rooms) });
        probe = await prepareTier(s, t);
        scene.add(s.root);
        loaded = s;
        shownTier = t;
        renderer.toneMapping = TONE[(manifest.tone_mapping || 'none').toLowerCase()] ?? NoToneMapping;
        renderer.toneMappingExposure = manifest.exposure ?? 1;
        shading.setAccentPoint(accentPoint(loaded));
        const m = screenMeshesOf(loaded);
        video.setMeshes(m.video);
        posters.setMeshes(m.posters);
        audio?.setSources(manifest.audio.sources, manifest.outdoor?.size_m ?? 0);
        slots = await loadSlots(manifest.scene_id);
        viewSlots(slots);
      } else if (slots) {
        // the same build: the slots stay, but a room with an overlay has one slot more than one without
        slots = await loadSlots(manifest.scene_id);
        viewSlots(slots);
      }
      // the room's sound: also when the scene stays (rooms.json audio is per room)
      audio?.setRoomSound(roomAudio());
      // the player, at the arrival portal facing into the room, or at the spawn
      player = new Player(manifest);
      // no arrival portal given: the target's door that leads back to the room we came from, if any
      // (else the door that leads to another room on the same scene: a connection set can send a Basement variant's
      // ladder to the hallway, whose ladder leads to another Basement variant; 2026-10-30, set "first")
      const sceneOf = (r: string | undefined) => (r ? roomInfo(rooms, r)?.scene : undefined);
      const fromScene = sceneOf(from);
      const back = spawnId ?? Object.entries(room.portals).find(([, l]) => l.to_room === from && !l.closed)?.[0]
        ?? manifest.portals?.find((x) => x.to_room === from && !room.portals[x.id])?.id
        ?? Object.entries(room.portals).find(([, l]) => !l.closed && fromScene && sceneOf(l.to_room) === fromScene)?.[0]
        ?? manifest.portals?.find((x) => !room.portals[x.id] && fromScene && sceneOf(x.to_room) === fromScene)?.id ?? null;
      const arrive = portalSpawn(manifest, back ?? undefined, room);
      if (arrive) {
        player.collider.settle(arrive.x, arrive.z);
        player.x = player.collider.x; player.z = player.collider.z; player.yaw = arrive.yaw;
      }
      player.apply(camera);
      audio?.updateListener(camera, true);
      setInteractive(loaded);
      ia.clearHover(); // nothing hovered in the new room until the pointer moves
      portals.disarmAt(player.x, player.z);
      if (slots) {
        artworks = new Artworks(renderer, scene, shading, slots, fileUrl);
        artworks.init();
        artworks.animate({ mobile, invalidate });
      }
      setupArt(artP);
      // room variables: the new room's come with the welcome; until then the defaults
      roomVars.receive({}, true);
      connectNet(arrive ? { x: player.x, z: player.z, yaw: player.yaw } : null);
      tmark('switch', t0);
      invalidate();
    } catch (e) {
      console.error('[rooms] switch failed', e);
      const msg = `Could not go to ${info.title}: ${(e as Error).message}`;
      ui.error(msg);
      ui.dismissHint();
      ui.notice(msg, 6000);
      // still in this room (the target did not load): the URL names it again (back / forward had changed it)
      if (!left && !push) history.replaceState({ room: roomId }, '', urlForRoom(roomId, home, undefined, rooms));
    } finally {
      switching = false;
      await fadeTo(0);
    }
  }
  // back / forward: the room in the URL
  addEventListener('popstate', () => {
    const target = roomFromUrl(rooms);
    if (target === roomId || !entered) return;
    // switchRoom arrives at the target room's door that leads here (back through the door we came in by)
    // a history entry of the first room keeps its ?scene= / ?channel= / ?build= overrides
    const u = new URLSearchParams(location.search);
    void switchRoom(target, null, false, { scene: u.get('scene'), channel: u.get('channel'), build: u.get('build') });
  });

  // test / debugging hooks
  (window as unknown as Record<string, unknown>).__lr = {
    renderer, scene, camera, get player() { return player; }, get manifest() { return manifest; }, get sceneBase() { return base; }, get room() { return roomId; }, rooms, portals, switchRoom: (r: string, spawn?: string) => switchRoom(r, spawn ?? null, true), get switching() { return switching; }, get loaded() { return loaded; }, get tier() { return shownTier; }, stats, adaptive, refresh, invalidate, timings, get probeBytes() { return probe?.bytes ?? 0; },
    interaction: ia, panel, roomVars, shading, retro, input, get artworks() { return artworks; }, get slots() { return slots; }, get artPanel() { return artPanel; }, get outline() { return outline; }, overlay,
    get audio() { return audio; }, get net() { return net; }, get items() { return items; }, get order() { return order; }, video, posters, get avatars() { return avatars; }, next: () => next(),
    gpu, get awake() { return awake; }, wake, leaveEditMode,
    teleport(x: number, z: number, yawDeg = player.yaw * 180 / Math.PI, pitchDeg = 0) {
      player.collider.settle(x, z);
      player.x = player.collider.x; player.z = player.collider.z;
      player.yaw = (yawDeg * Math.PI) / 180; player.pitch = (pitchDeg * Math.PI) / 180;
      player.apply(camera); invalidate();
    },
  };
}

/**
 * Where the accent wash of the baked approximation centres: in front of the centre panel (tint id
 * centre_panel), else in front of the monitor, else the room centre.
 */
function accentPoint(s: LoadedScene): Vector3 {
  const box = new Box3();
  for (const mesh of s.tintables.get('centre_panel') ?? []) box.expandByObject(mesh);
  const room = new Box3().setFromObject(s.root);
  const centre = room.getCenter(new Vector3());
  if (box.isEmpty()) {
    for (const mesh of s.screens.values()) if (mesh.name === 'monitor_display') box.expandByObject(mesh);
  }
  if (box.isEmpty()) return centre.setY(room.min.y + 1.3);
  const p = box.getCenter(new Vector3());
  // 0.5 m toward the room centre (in plan)
  const d = new Vector3(centre.x - p.x, 0, centre.z - p.z);
  if (d.lengthSq() > 1e-6) p.addScaledVector(d.normalize(), 0.5);
  return p;
}

/**
 * Voyage by default (?source=local for the files in public/media); falls back to local if Voyage fails.
 * `shuffle`: Voyage's per-playlist setting (data/playlists.json); local media plays in list order.
 */
async function loadMediaList(): Promise<{ items: MediaItem[]; shuffle: boolean }> {
  const local = async () => ({ items: await new LocalSource().list().catch(() => [] as MediaItem[]), shuffle: false });
  if (config.source === 'local') return local();
  try {
    const source = new VoyageSource();
    const [items, shuffle] = await Promise.all([source.list(), source.shuffles()]);
    if (items.length) return { items, shuffle };
    console.warn('[media] the Voyage list is empty; using local media');
  } catch (e) {
    console.warn('[media] Voyage list failed; using local media', e);
  }
  return local();
}

main().catch((e) => {
  console.error(e);
  ui.error(String((e as Error).message || e));
});
